/* eslint-disable react/jsx-props-no-spreading */
import CategoryTile from '../../molecules/categoryTile';
import { Heading } from '../../typography/heading/Heading';
import { Text } from '../../typography/text/Text';
import Title from '../../typography/title';
import mergeClasses from '../../../utils/shallowMerge';
import React, { useMemo } from 'react';
import { Navigation, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import defaultClasses from './CategoriesSectionAutoplayCarousel.module.scss';
import { autoplayCarouselSlidesPerView } from './_vars';
import { ArrowLeft, ArrowRight } from '../../atoms/icons';
import { StylesCustomizer } from '../../molecules/stylesCustomizer';

const CategoriesSectionAutoplayCarousel = ({
  categories,
  label,
  labelStyles,
  title,
  titleStyles,
  customClasses,
  customClassesForCategoryTile,
  tileLayout = 'stacked',
}) => {
  const styles = mergeClasses(defaultClasses, customClasses);

  const breakpoints = {
    320: {
      spaceBetween: 8,
      slidesPerView: autoplayCarouselSlidesPerView.mobile,
      initialSlide: 1,
      enabled: categories.length > autoplayCarouselSlidesPerView.mobile,
      autoplay: {
        pauseOnMouseEnter: false,
      },
    },
    680: {
      spaceBetween: 15,
      slidesPerView: autoplayCarouselSlidesPerView.tablet,
      initialSlide: 1,
      enabled: categories.length > autoplayCarouselSlidesPerView.tablet,
      autoplay: {
        pauseOnMouseEnter: false,
      },
    },
    1120: {
      spaceBetween: 25,
      slidesPerView: autoplayCarouselSlidesPerView.desktop,
      initialSlide: 1,
      enabled: categories.length > autoplayCarouselSlidesPerView.desktop,
    },
    2560: {
      spaceBetween: 35,
      slidesPerView: autoplayCarouselSlidesPerView.desktop,
      initialSlide: 1,
      enabled: categories.length > autoplayCarouselSlidesPerView.desktop,
    },
  };

  const LabelText = () => {
    return (
      <Text className={styles.cateogryLabel} as="span" weight="normal" textTransform="uppercase">
        <span
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />
      </Text>
    );
  };

  const TitleText = (variables) => {
    return (
      <Heading
        align="center"
        className={styles.categoryHeader}
        as="h1"
        placement="plp"
        textTransform="uppercase"
        weight="light"
        variables={variables}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
      </Heading>
    );
  };

  const WrapperText = (props) => {
    return (
      <Title className={styles.categoryTitle} variables={props}>
        {props?.children}
      </Title>
    );
  };

  const StyledWrapperText = StylesCustomizer(WrapperText);
  const StyledLabelText = StylesCustomizer(LabelText);
  const StyledTitleText = StylesCustomizer(TitleText);

  const categoriesTitle =
    label || title ? (
      <StyledWrapperText styles={labelStyles}>
        {label && <StyledLabelText />}
        {title && <StyledTitleText styles={titleStyles} />}
      </StyledWrapperText>
    ) : null;

  if (!categories || !Array.from(categories).length) return null;

  const swiperCategories = useMemo(() => {
    return categories ? (
      <div className={`${styles.popularCategoriesSection}`}>
        <Swiper
          breakpoints={breakpoints}
          autoplay={{
            delay: 5000,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
          }}
          navigation={{
            prevEl: '.swiper-prev-arrow',
            nextEl: '.swiper-next-arrow',
            lockClass: styles.hiddenClass,
          }}
          modules={[Navigation, Autoplay]}
          loop={true}
        >
          {categories.map((cat, index) => {
            const imageMd = cat.imageHeightMd || cat.imageHeightLg;
            const imageSm = cat.imageHeightsm ? cat.imageHeightsm : imageMd;

            return (
              <SwiperSlide key={index}>
                <CategoryTile
                  style={{
                    '--image-height-lg': cat.imageHeightLg ? `${cat.imageHeightLg}%` : '125%',
                    '--image-height-md': imageMd ? `${imageMd}%` : '125%',
                    '--image-height-sm': imageSm ? `${imageSm}%` : '125%',
                  }}
                  align={cat.contentAlignment}
                  titleLink={cat.categoryUrl}
                  title={cat.teaserText && cat.teaserText}
                  titleStyles={cat.titleStyles}
                  description={cat.teaserDescription}
                  descriptionStyles={cat.descriptionStyles}
                  image={cat.categoryImage}
                  links={cat.subcats && cat.subcats}
                  isFeatured={cat.isFeatured}
                  customClasses={{
                    ...customClassesForCategoryTile,
                    title: styles.title,
                  }}
                  linkObj={cat.categoryUrl}
                  tileLayout={tileLayout}
                />
              </SwiperSlide>
            );
          })}
          <div className={styles.sliderButtonWrap}>
            <div
              data-carousel-arrow="arrow_left"
              className={`swiper-prev-arrow ${styles.arrowNavigation} ${styles.arrowNavigationLeft}`}
            >
              <ArrowLeft width="30px" height="20px" />
            </div>
            <div
              data-carousel-arrow="arrow_right"
              className={`swiper-next-arrow ${styles.arrowNavigation} ${styles.arrowNavigationRight}`}
            >
              <ArrowRight width="30px" height="20px" />
            </div>
          </div>
        </Swiper>
      </div>
    ) : null;
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.categoriesGrid}>
        {categoriesTitle}
        {swiperCategories}
      </div>
    </div>
  );
};

export default CategoriesSectionAutoplayCarousel;
